/**
 * ReimpresionTicketModal.tsx
 *
 * Modal para reimprimir un ticket existente.
 * NO modifica estado del ticket ni afecta saldos.
 * Solo consulta datos y genera payload de reimpresión.
 *
 * En APK SUNMI: imprime directamente sin abrir preview.
 * En Web: abre preview modal para imprimir.
 *
 * Busca por número de ticket dentro del tenant actual (banca + local).
 */
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from '../../../components/ui';
import { Search, Printer, AlertTriangle, Clock, CheckCircle } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import {
  procesarReimpresion,
  TicketPayload,
  TicketJugada,
} from '../../../services/ticketService';
import { formatTimeCR } from '../../../utils/date';
import { formatTicketForPrint } from '../../../utils/ticketFormat';
import { reprintTicketByDestination, isNativePrintSelected } from '../../../utils/printer';

interface ReimpresionResponse {
  ticket_id: string;
  ticket_number: string;
  estado: string;
  total: number;
  metodo_pago: string;
  fecha: string;
  cliente_nombre: string;
  referencia: string | null;
  sorteo_nombre: string;
  sorteo_cierre: string;
  sorteo_fecha: string;
  fecha_operativa: string | null;
  jugadas: Array<{ num1: string; num2?: string | null; monto: number }>;
  local?: {
    nombre: string;
    descripcion?: string | null;
    telefono?: string | null;
    logo?: string | null;
    es_propio?: boolean;
    grupo_id?: string | null;
  };
  es_propio?: boolean;
  es_reimpresion: boolean;
}

interface TicketRecienteItem {
  id: string;
  ticket_number: string | null;
  cliente_nombre: string | null;
  created_at: string;
  total: number;
  estado: string;
}

interface ReimpresionTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReimprimir: (payload: TicketPayload) => void;
}

export const ReimpresionTicketModal: React.FC<ReimpresionTicketModalProps> = ({
  isOpen,
  onClose,
  onReimprimir,
}) => {
  const [busqueda, setBusqueda] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ticketData, setTicketData] = useState<ReimpresionResponse | null>(null);
  const [ticketsRecientes, setTicketsRecientes] = useState<TicketRecienteItem[]>([]);
  const [loadingRecientes, setLoadingRecientes] = useState(false);
  const [printSuccess, setPrintSuccess] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSunmi = isNativePrintSelected();

  const fetchRecientes = async () => {
    setLoadingRecientes(true);
    try {
      const data = await fetchAPI<TicketRecienteItem[]>('/tickets/recientes?limit=100');
      setTicketsRecientes(data);
    } catch {
      // No bloquear el modal si falla la carga de recientes
      setTicketsRecientes([]);
    } finally {
      setLoadingRecientes(false);
    }
  };

  // Reset al abrir y cargar recientes
  useEffect(() => {
    if (!isOpen) return;
    setBusqueda('');
    setError(null);
    setTicketData(null);
    setLoading(false);
    setTicketsRecientes([]);
    setPrintSuccess(false);
    fetchRecientes();
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  const buscarTicket = async () => {
    const codigo = busqueda.trim();
    if (!codigo) {
      setError('Ingresa el número de ticket');
      return;
    }

    setLoading(true);
    setError(null);
    setTicketData(null);

    try {
      // Buscar ticket por ID o ticket_number en el backend
      const data = await fetchAPI<ReimpresionResponse>(
        `/tickets/${encodeURIComponent(codigo)}/reimpresion`,
        { method: 'POST' }
      );
      setTicketData(data);
    } catch (e: any) {
      setError(e.message || 'Ticket no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      buscarTicket();
    }
  };

  const handleSeleccionarReciente = (ticket: TicketRecienteItem) => {
    const codigo = ticket.ticket_number || ticket.id;
    setBusqueda(codigo);
    setError(null);
    setTicketData(null);
    // Lanzar búsqueda automáticamente
    setLoading(true);
    fetchAPI<ReimpresionResponse>(
      `/tickets/${encodeURIComponent(codigo)}/reimpresion`,
      { method: 'POST' }
    )
      .then(data => {
        setTicketData(data);
      })
      .catch((e: any) => setError(e.message || 'Ticket no encontrado'))
      .finally(() => setLoading(false));
  };

  const formatHora = (isoString: string) => {
    try {
      return formatTimeCR(isoString);
    } catch {
      return isoString;
    }
  };

  const handleReimprimir = async () => {
    if (!ticketData) return;
    if (loading) return; // Guard doble-click

    console.log('[APK REPRINT] ─────────────────────────────────');
    console.log('[APK REPRINT] step 1 click');
    console.log('[APK REPRINT] ticket:', ticketData.ticket_number);
    console.log('[APK REPRINT] isSunmi:', isSunmi);

    setLoading(true);
    setError(null);
    setPrintSuccess(false);

    // Safety timeout: GARANTIZA que loading se limpia en max 10 segundos
    const safetyTimer = setTimeout(() => {
      console.error('[APK REPRINT] SAFETY TIMEOUT 10s - forzando cleanup');
      setLoading(false);
      setError('Timeout: la impresión tardó demasiado. Intenta de nuevo.');
    }, 10000);

    try {
      console.log('[APK REPRINT] step 2 payload recibido, construyendo jugadas...');
      const jugadas: TicketJugada[] = ticketData.jugadas.map(j => ({
        num1: j.num1,
        num2: j.num2 || undefined,
        monto: j.monto,
      }));
      console.log('[APK REPRINT] jugadas:', jugadas.length);

      const payload = await procesarReimpresion({
        ticketCode: formatTicketForPrint(ticketData.ticket_number),
        ticketId: ticketData.ticket_number,
        sorteoNombre: ticketData.sorteo_nombre,
        sorteoCierre: ticketData.sorteo_cierre,
        sorteoFecha: ticketData.sorteo_fecha,
        metodoPago: ticketData.metodo_pago,
        referencia: ticketData.referencia,
        total: ticketData.total,
        // Fecha/hora original de venta — la copia debe ser idéntica al original.
        fecha: ticketData.fecha || null,
        jugadas,
        local: {
          nombre: ticketData.local?.nombre || '',
          descripcion: ticketData.local?.descripcion || null,
          telefono: ticketData.local?.telefono || null,
          logo: ticketData.local?.logo || null,
          esPropio: ticketData.local?.es_propio === true,
          grupoId: ticketData.local?.grupo_id || null,
        },
        esPropio: ticketData.local?.es_propio === true,
        clienteNombre: ticketData.cliente_nombre || 'Cliente',
        // Backend pre-computes fecha_operativa by comparing draw date vs sell date.
        // null means same-day ticket; a DD/MM/YYYY string means future-sale ticket.
        fechaOperativa: ticketData.fecha_operativa || null,
      });
      console.log('[APK REPRINT] payload procesado OK');

      // APK nativa: imprimir directamente sin modal
      if (isSunmi) {
        console.log('[APK REPRINT] step 3 entrando ruta nativa (reprintTicketByDestination)');

        const result = await reprintTicketByDestination({
          ticketCode: payload.ticketCode,
          sorteoNombre: payload.sorteoNombre,
          fecha: payload.fecha,
          total: payload.total,
          jugadas: payload.jugadas,
          localNombre: payload.localNombre,
          localDescripcion: payload.local?.descripcion || undefined,
          sorteoCierre: payload.sorteoCierre,
          vendedorNombre: payload.vendedorNombre,
          clienteNombre: payload.clienteNombre,
          pagaVecesAplicado: payload.pagaVecesAplicado,
          leyendaLocal: payload.config?.leyendaLocal || undefined,
          localTelefono: payload.local?.telefono || undefined,
          fechaOperativa: payload.fechaOperativa || undefined,
        });

        if (result.ok) {
          console.log('[APK REPRINT] step 5 success');
          setPrintSuccess(true);
          setTimeout(() => onClose(), 1500);
        } else {
          console.error('[APK REPRINT] step 5 falló:', result.error);
          setError(result.error || 'Error al reimprimir');
        }
      } else {
        // Web: abrir modal de preview
        console.log('[APK REPRINT] ruta WEB (abriendo modal preview)');
        onReimprimir(payload);
        onClose();
      }
    } catch (e: any) {
      const errMsg = e?.message || String(e) || 'Error desconocido';
      console.error('[APK REPRINT] step 5 error:', errMsg);
      setError(errMsg);
    } finally {
      clearTimeout(safetyTimer);
      setLoading(false);
      console.log('[APK REPRINT] step 6 finally - loading cleared');
      console.log('[APK REPRINT] ─────────────────────────────────');
    }
  };

  const estadoBadge = (estado: string) => {
    switch (estado) {
      case 'ABIERTO':
        return 'bg-green-100 text-green-800';
      case 'ANULADO':
        return 'bg-red-100 text-red-800';
      case 'PREMIADO':
        return 'bg-yellow-100 text-yellow-800';
      case 'PAGADO':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Reimpresión de Ticket" size="md">
      <div className="space-y-4">
        {/* Campo de búsqueda */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Número de ticket (ej: T-001)"
              className="w-full pl-9 pr-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
              autoComplete="off"
              disabled={loading}
            />
          </div>
          <Button
            variant="primary"
            onClick={buscarTicket}
            disabled={loading || !busqueda.trim()}
            isLoading={loading && !ticketData}
          >
            Buscar
          </Button>
        </div>

        {/* Tickets recientes del día */}
        {!ticketData && (
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Clock size={13} className="text-gray-400" />
              <span className="text-xs text-gray-400 uppercase tracking-wide font-medium">Tickets recientes</span>
            </div>
            {loadingRecientes ? (
              <p className="text-xs text-gray-400 py-2 text-center">Cargando...</p>
            ) : ticketsRecientes.length === 0 ? (
              <p className="text-xs text-gray-400 py-2 text-center">No hay tickets registrados hoy</p>
            ) : (
              <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {ticketsRecientes.map(t => (
                  <button
                    key={t.id}
                    onClick={() => handleSeleccionarReciente(t)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors flex items-center justify-between gap-2"
                    disabled={loading}
                  >
                    <div className="min-w-0">
                      <span className="font-mono text-sm font-bold text-gray-800">
                        {t.ticket_number ? formatTicketForPrint(t.ticket_number) : t.id.slice(0, 8)}
                      </span>
                      {t.cliente_nombre && (
                        <span className="ml-2 text-xs text-gray-500 truncate">{t.cliente_nombre}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-gray-400">{formatHora(t.created_at)}</span>
                      <span className="text-xs font-bold text-gray-700">
                        ₡{t.total.toLocaleString()}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
            <AlertTriangle size={16} />
            {error}
          </div>
        )}

        {/* Success (APK SUNMI) */}
        {printSuccess && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm">
            <CheckCircle size={16} />
            ✓ Reimpresión enviada a SUNMI
          </div>
        )}

        {/* Datos del ticket encontrado */}
        {ticketData && (
          <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-lg font-bold text-gray-900">
                {formatTicketForPrint(ticketData.ticket_number)}
              </span>
              <span className={`text-xs font-bold px-2 py-1 rounded ${estadoBadge(ticketData.estado)}`}>
                {ticketData.estado}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
              <div>
                <span className="text-gray-400">Sorteo:</span>{' '}
                <span className="font-medium text-gray-800">{ticketData.sorteo_nombre}</span>
              </div>
              <div>
                <span className="text-gray-400">Cierre:</span>{' '}
                <span className="font-medium text-gray-800">{ticketData.sorteo_cierre}</span>
              </div>
              <div>
                <span className="text-gray-400">Fecha:</span>{' '}
                <span className="font-medium text-gray-800">{ticketData.fecha}</span>
              </div>
              <div>
                <span className="text-gray-400">Pago:</span>{' '}
                <span className="font-medium text-gray-800">{ticketData.metodo_pago}</span>
              </div>
              {ticketData.cliente_nombre && (
                <div className="col-span-2">
                  <span className="text-gray-400">Cliente:</span>{' '}
                  <span className="font-medium text-gray-800">{ticketData.cliente_nombre}</span>
                </div>
              )}
            </div>

            {/* Jugadas */}
            <div className="border-t pt-2">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Jugadas ({ticketData.jugadas.length})</p>
              <div className="relative max-h-32 overflow-y-auto space-y-0.5">
                {/* Sello de agua */}
                <span
                  className="pointer-events-none select-none absolute inset-0 flex items-center justify-center text-red-500 font-extrabold text-2xl tracking-widest uppercase"
                  style={{ opacity: 0.12, transform: 'rotate(-30deg)', zIndex: 1 }}
                >
                  REIMPRESIÓN
                </span>
                {ticketData.jugadas.map((j, i) => (
                  <div key={i} className="relative flex justify-between text-sm font-mono" style={{ zIndex: 2 }}>
                    <span className="text-gray-700">
                      {j.num1}{j.num2 ? `-${j.num2}` : ''}
                    </span>
                    <span className="font-bold text-gray-900">
                      ₡{j.monto.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Total */}
            <div className="border-t pt-2 flex justify-between items-center">
              <span className="font-bold text-gray-700">TOTAL</span>
              <span className="text-xl font-bold text-gray-900">
                ₡{ticketData.total.toLocaleString()}
              </span>
            </div>

            {/* Nota */}
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded">
              La reimpresión no modifica el ticket original ni genera nuevo código.
            </p>

            {/* Botón reimprimir */}
            <Button
              variant="primary"
              onClick={handleReimprimir}
              disabled={loading || printSuccess}
              isLoading={loading}
              className="w-full flex items-center justify-center gap-2"
            >
              <Printer size={18} />
              {printSuccess ? '✓ Impreso' : (isSunmi ? 'Imprimir Directo' : 'Reimprimir Ticket')}
            </Button>
          </div>
        )}

        {/* Botón cerrar */}
        <div className="flex justify-end pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReimpresionTicketModal;
